import React from 'react';

import { FormattedMessage } from 'react-intl-next';
import { Link } from 'react-resource-router';

import Button from '@atlaskit/button/standard-button';
import BitbucketCommitsIcon from '@atlaskit/icon/glyph/bitbucket/commits';
import Tooltip from '@atlaskit/tooltip';

import { Expander } from 'src/components/sidebar';
import { Commit } from 'src/components/types';
import { useIntl } from 'src/hooks/intl';
import { publishUiEvent } from 'src/utils/analytics/publish';
import { shortHash } from 'src/utils/short-hash';

import messages from './commit-page.i18n';
import { CommitCardWrapper } from './commit-page.styled';

const CommitsIcon = (): JSX.Element => {
  return <BitbucketCommitsIcon label="" size="medium" />;
};

export const ExpandCommitParents = ({
  commit,
}: {
  commit: Commit;
}): JSX.Element => {
  const parents = commit.parents || [];

  const parentLinks = parents.map((parent, i) => [
    i > 0 && ', ',
    <Link key={i} href={`${parent.hash}`}>
      {shortHash(parent.hash)}
    </Link>,
  ]);
  let message;
  if (parents.length === 1) message = messages.oneParentCommit;
  else message = messages.parentCommits;

  const label = (
    <FormattedMessage
      {...message}
      values={{ count: <strong>{parents.length}</strong> }}
    />
  );
  return (
    <Expander icon={<CommitsIcon />} label={label}>
      {parents.length > 0 && (
        <CommitCardWrapper>{parentLinks}</CommitCardWrapper>
      )}
    </Expander>
  );
};

export const CollapseCommitParents = ({
  commit,
}: {
  commit: Commit;
}): JSX.Element => {
  const intl = useIntl();
  const parents = commit.parents || [];
  let message;
  if (parents.length === 1) message = messages.oneParentCommit;
  else message = messages.parentCommits;

  const label = (
    <FormattedMessage {...message} values={{ count: parents.length }} />
  );
  return (
    <Tooltip content={label} position="left">
      <Button
        onClick={() =>
          publishUiEvent({
            action: 'clicked',
            actionSubject: 'link',
            actionSubjectId: 'parentCommitLink',
            source: 'commitPage',
          })
        }
        appearance="subtle"
        label={intl.formatMessage(messages.parentCommitsLabel)}
        iconBefore={<CommitsIcon />}
      />
    </Tooltip>
  );
};
